/**
 * Where the map starts — ENG-277.
 *
 * Band-aid until global / user-scoped settings land: open the map at the
 * viewer's own location (browser Geolocation API) instead of the tenant default
 * (Edmonton). When settings arrive, `getStartCenter` becomes
 * user setting → tenant setting → browser location → tenant default.
 *
 * The last fix is kept in localStorage — this browser only, never sent
 * anywhere — so a returning visitor opens straight at their location instead
 * of jumping from Edmonton once the (already granted) fix comes back.
 *
 * All coordinates here are `[lat, lng]`, matching `config.map.defaultCenter`.
 */
import { config } from '@/lib/config';

export type LatLng = [number, number];

const STORAGE_KEY = 'flysafe-v2-last-location';

/** City-level is all the start view needs, and low accuracy answers faster. */
const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  // Only counts once permission is granted — an unanswered prompt never
  // times out, which is fine because nothing waits on this.
  timeout: 8000,
  maximumAge: 10 * 60 * 1000,
};

function isLatLng(v: unknown): v is LatLng {
  return (
    Array.isArray(v) &&
    v.length === 2 &&
    typeof v[0] === 'number' &&
    typeof v[1] === 'number' &&
    Math.abs(v[0]) <= 90 &&
    Math.abs(v[1]) <= 180
  );
}

/** Last location this browser reported, or null. Storage can be blocked
 *  (private mode, disabled site data), so every access is guarded. */
export function getCachedLocation(): LatLng | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const v: unknown = raw ? JSON.parse(raw) : null;
    return isLatLng(v) ? v : null;
  } catch {
    return null;
  }
}

function cacheLocation(p: LatLng): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable — the live fix still applies this session */
  }
}

/** Where a map should open: the last known user location, else the tenant default. */
export function getStartCenter(): LatLng {
  return getCachedLocation() ?? config.map.defaultCenter;
}

/**
 * Ask the browser for the viewer's location (prompts on first use). Resolves
 * null when unsupported, denied, unavailable or timed out — the caller just
 * keeps the default view. A successful fix is cached for the next visit.
 */
export function requestUserLocation(): Promise<LatLng | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p: LatLng = [pos.coords.latitude, pos.coords.longitude];
        cacheLocation(p);
        resolve(p);
      },
      () => resolve(null),
      GEO_OPTIONS,
    );
  });
}
